import { Button, CircularProgress } from '@material-ui/core';
import ArrowForwardIosIcon from '@material-ui/icons/ArrowForwardIos';
import React, { Component } from 'react';
import { Field, Form } from 'react-final-form';
import { withTranslation, WithTranslation } from 'react-i18next';
import { required, requiredAutocomplete } from 'utils/customValidators';
import { kenticoSdk } from 'utils/services/kenticoSdk.service';
import { IWithAddContent, withAddContent } from '../HOC/WithAddContent';
import addContentParser from '../HOC/WithAddContent.parser';
import CustomDatepicker from '../Input/CustomDatepicker';
import CustomField from '../Input/CustomField';
import CustomSelect from '../Input/CustomSelect/CustomSelect';
import CustomTextArea from '../Input/CustomTextArea';
import DraggableSelect from '../Input/DraggableSelect/DraggableSelect';
import {
  AddContenRowContainer,
  AddContentFormBox,
  AddContentFormButtonContainer,
  AddContentFormContainer,
  AddContentFormDiv,
  AddContentFormHeader,
  AddContentFormMainContainer,
  ArrowSvgIconContainer,
} from './AddContentForm.styled';

interface IState {
  isProjectNameDisabled: boolean;
}

type PropType = WithTranslation & IWithAddContent;

export class AddContentForm extends Component<PropType, IState> {
  private parser: typeof addContentParser;

  constructor(props: PropType) {
    super(props);
    this.parser = addContentParser;

    this.state = {
      isProjectNameDisabled: false,
    };
  }

  componentDidMount(): void {
    const { getCustomers, getProjectPrefix, getAvailableLanguages, setSelectedEntries, form } = this.props;

    form.subscribe(
      ({ values: { activeProject } }) => {
        if (!!activeProject) {
          this.setState({ isProjectNameDisabled: true });
        } else {
          this.setState({ isProjectNameDisabled: false });
        }
      },
      { values: true },
    );

    setSelectedEntries([]);
    getCustomers();
    getAvailableLanguages();
    getProjectPrefix();

    const itemId = kenticoSdk?.context?.item?.id;

    if (itemId) {
      kenticoSdk.CustomElement?.getItemDetails([itemId]).then((result) => {
        if (result?.length) {
          setSelectedEntries([
            {
              id: itemId,
              name: result[0]?.name,
              codeName: result[0]?.codename,
              dueDate: new Date(),
              lastModification: new Date(),
              publishedDate: new Date(),
              typeCodeName: '',
              typeName: '',
              workflowStep: '',
            },
          ]);
        }
      });
    }
  }

  render(): JSX.Element {
    const {
      t,
      xtmCustomer,
      xtmTemplate,
      availableLanguages,
      activeProjects,
      xtmCustomerSpinner,
      xtmTemplateSpinner,
      activeProjectsSpinner,
      languagesSpinner,
      translateSpinner,
      contentSpinner,
      defaultSourceLanguage,
      defaultCustomer,
      defaultTemplate,
      handleCustomerChange,
      handleSourceLanguageChange,
      handleTargetLanguagesChange,
      handleSubmitForm,
      contentState: { selected },
      targetLanguages,
      form,
      defaultDueDate,
    } = this.props;

    const parsed = {
      customers: this.parser.parseCustomers(xtmCustomer),
      templates: this.parser.parseTemplates(xtmTemplate),
      activeProjects: this.parser.parseProjects(activeProjects),
    };

    const { isProjectNameDisabled } = this.state;

    return (
      <AddContentFormDiv>
        <Form
          // eslint-disable-next-line @typescript-eslint/no-empty-function
          onSubmit={(): void => {}}
          subscription={{ pristine: true, submitting: true }}
          form={form}
          render={({ handleSubmit }): JSX.Element => (
            <AddContentFormMainContainer onSubmit={handleSubmit}>
              <AddContentFormContainer>
                <AddContentFormHeader>{t('addcontent.sendContentForTranslation')}</AddContentFormHeader>
                <AddContentFormBox>
                  <AddContenRowContainer>
                    <Field
                      name="xtmCustomer"
                      validate={requiredAutocomplete}
                      component={CustomSelect}
                      label="addcontent.xtmCustomer"
                      options={parsed.customers}
                      onChange={handleCustomerChange}
                      spinner={xtmCustomerSpinner}
                      initialValue={defaultCustomer}
                      placeholder={t('common.selectPlaceholder')}
                      disableClearable
                    />
                  </AddContenRowContainer>

                  <AddContenRowContainer>
                    <Field
                      name="xtmTemplate"
                      component={CustomSelect}
                      validate={requiredAutocomplete}
                      label="addcontent.xtmTemplate"
                      options={parsed.templates}
                      spinner={xtmTemplateSpinner}
                      initialValue={defaultTemplate}
                      placeholder={t('common.selectPlaceholder')}
                      disableClearable
                    />
                  </AddContenRowContainer>

                  <AddContenRowContainer>
                    <Field
                      name="sourceLanguage"
                      validate={requiredAutocomplete}
                      component={CustomSelect}
                      disabled={contentSpinner}
                      label="addcontent.sourceLanguage"
                      options={this.parser.parseLanguages(availableLanguages)}
                      onChange={handleSourceLanguageChange}
                      spinner={languagesSpinner}
                      initialValue={defaultSourceLanguage}
                      placeholder={t('common.selectPlaceholder')}
                      disableClearable
                    />

                    <ArrowSvgIconContainer>
                      <ArrowForwardIosIcon />
                    </ArrowSvgIconContainer>

                    <Field
                      name="targetLanguages"
                      component={DraggableSelect}
                      label="addcontent.targetLanguages"
                      options={this.parser.parseLanguages(targetLanguages)}
                      onChange={handleTargetLanguagesChange}
                      spinner={languagesSpinner}
                      validate={requiredAutocomplete}
                      placeholder={t('common.selectPlaceholder')}
                    />
                  </AddContenRowContainer>

                  <AddContenRowContainer>
                    <Field
                      name="projectName"
                      component={CustomField}
                      validate={isProjectNameDisabled ? undefined : required}
                      key={isProjectNameDisabled ? 'projectNameNoValidate' : 'projectName'}
                      disabled={isProjectNameDisabled}
                      label="addcontent.projectName"
                    />
                  </AddContenRowContainer>

                  <AddContenRowContainer>
                    <Field
                      name="description"
                      component={CustomTextArea}
                      label="addcontent.description"
                      disabled={isProjectNameDisabled}
                    />
                  </AddContenRowContainer>

                  <AddContenRowContainer>
                    <Field
                      name="activeProject"
                      component={CustomSelect}
                      label="addcontent.activeXtmProjects"
                      options={parsed.activeProjects}
                      spinner={activeProjectsSpinner}
                      placeholder={t('common.selectPlaceholder')}
                    />
                  </AddContenRowContainer>

                  <AddContenRowContainer>
                    <Field
                      name="dueDate"
                      component={CustomDatepicker}
                      initialValue={defaultDueDate}
                      label="addcontent.dueDate"
                      placeholder={t('common.selectPlaceholder')}
                    />
                  </AddContenRowContainer>
                </AddContentFormBox>
              </AddContentFormContainer>
              <AddContentFormButtonContainer>
                <Button
                  size="medium"
                  variant="outlined"
                  color="primary"
                  onClick={handleSubmitForm(true)}
                  disabled={!Boolean(selected?.length) || translateSpinner}
                >
                  {translateSpinner ? <CircularProgress size="24px" color="secondary" /> : t('addcontent.addToQueue')}
                </Button>
                <Button
                  size="medium"
                  variant="contained"
                  color="secondary"
                  onClick={handleSubmitForm(false)}
                  disabled={!Boolean(selected?.length) || translateSpinner}
                >
                  {translateSpinner ? (
                    <CircularProgress size="24px" color="secondary" />
                  ) : (
                    t('addcontent.sendForTranslation')
                  )}
                </Button>
              </AddContentFormButtonContainer>
            </AddContentFormMainContainer>
          )}
        />
      </AddContentFormDiv>
    );
  }
}

export default withAddContent(withTranslation()(AddContentForm));
